import { Injectable } from '@angular/core';
import {Apollo} from "apollo-angular";
import {
  add_certificated_participant,
  add_fund_participant, create_fund_investor,
  create_fund_owned_entity, create_fund_ownership_interest,
  create_investment_detail,
  create_new_budget_revenue_detail,
  create_owned_entity_attribute, create_project_credit, create_project_credit_outlet,
  delete_budget_revenue_detail,
  delete_fund_investment_detail, delete_fund_investor, delete_project_credit_outlet,
  remove_participant,
  update_budget_revenue_year,
  update_fund_details,
  update_fund_investor, update_fund_managing_member,
  update_investment_detail,
  update_participants, update_project_credit
} from "./graphql/updateFunds";
import {DatePipe} from "@angular/common";
import {create_owned_entity} from "../deals/deals-home/graphql/getProjectDetails";
import {participants_search_people} from "../deals/deals-subcomponents/participants/graphql/participantsSearchPeople";
import {AlertService} from "../alert/alert.service";
import {nav_search_organizations} from "../side-nav/cw-search/graphql/navSearchOrganizations";
import {
  credit_outlets_search_projects,
  get_certificated_outlet,
  get_fund,
  search_organizations
} from "./graphql/getFunds";
import {search_active_employees} from "../company-events/graphql/getCompanyEvents";
import {BehaviorSubject} from "rxjs";
import {MatTableDataSource} from "@angular/material/table";
import {CREATable, ThirdPartyTable} from "../deals/deals-subcomponents/participants/participants.component";
import {DataFormatService} from "../services/data-format.service";

export enum UpdateFundOutletCreditMode {ADD, UPDATE, REMOVE}
export enum UpdateFundBudgetYearMode {ADD, UPDATE, REMOVE}

@Injectable({
  providedIn: 'root'
})
export class FundService {

  constructor(private apollo: Apollo, private datePipe: DatePipe, private dataFormat: DataFormatService, private alertService: AlertService) { }

  private _loaded:boolean = false;
  private loaded = new BehaviorSubject(this._loaded);
  public loaded$ = this.loaded.asObservable();

  private _investors:any[] = []
  private investors = new BehaviorSubject(this._investors)
  public investors$ = this.investors.asObservable();

  private _investmentClasses:any[] = [];
  private investmentClasses = new BehaviorSubject(this._investmentClasses)
  public investmentClasses$ = this.investmentClasses.asObservable();

  private _fund:any = {};
  private fund = new BehaviorSubject(this._fund);
  public fund$ = this.fund.asObservable();

  private _permissions:any = {};
  private permissions = new BehaviorSubject(this._permissions);
  public permissions$ = this.permissions.asObservable();

  private _participants:{[key:string]: any} = {
    ThirdPartyParticipants: {
      color: 'orange',
      title : 'Third Party Participants',
      dataSource: new MatTableDataSource([]),
      columns: ['role', 'participant', 'sponsor'],
      editColumns: ['role', 'participant', 'sponsor', 'actions']
    },
    CREAParticipants: {
      color: 'orange-dark',
      title: 'CREA Participants',
      dataSource: new MatTableDataSource([]),
      columns: ['role', 'participant'],
      editColumns: ['role', 'participant', 'actions']
    }
  }
  private participants = new BehaviorSubject(this._participants);
  public participants$ = this.participants.asObservable();

  private _outlets:{[key:string]: any} = {}
  private outlets = new BehaviorSubject(this._outlets)
  public outlets$ = this.outlets.asObservable();

  private _CREAParticipantRoles:any[] = [];
  private CREAParticipantRoles = new BehaviorSubject(this._CREAParticipantRoles)
  public CREAParticipantRoles$ = this.CREAParticipantRoles.asObservable();

  private _thirdParticipantRoles:any[] = []
  private thirdParticipantRoles = new BehaviorSubject(this._thirdParticipantRoles);
  public thirdParticipantRoles$ = this.thirdParticipantRoles.asObservable();

  private _sequenceStatuses:any[] = [];
  private sequenceStatuses = new BehaviorSubject(this._sequenceStatuses);
  public sequenceStatuses$ = this.sequenceStatuses.asObservable();

  private _fundProjectStates:any[] = [];
  private fundProjectStates = new BehaviorSubject(this._fundProjectStates);
  public fundProjectStates$ = this.fundProjectStates.asObservable();

  private _fundTypes:any[] = [];
  private fundTypes = new BehaviorSubject(this._fundTypes)
  public fundTypes$ = this.fundTypes.asObservable();

  private _investorTypeId:string | null = null;
  private investorTypeId = new BehaviorSubject(this._investorTypeId)
  public investorTypeIdS = this.investorTypeId.asObservable();

  private _syndicationsSequences:any[] = [];
  private syndicationsSequences = new BehaviorSubject(this._syndicationsSequences)
  public syndicationsSequences$ = this.syndicationsSequences.asObservable();

  private _specialPurposeEntityId:string | null = null;
  private specialPurposeEntityId = new BehaviorSubject(this._specialPurposeEntityId)
  public specialPurposeEntityId$ = this.specialPurposeEntityId.asObservable();


  public setInvestors(investors:any[]){
    this._investors = investors;
    this.investors.next(this._investors)
  }

  private _setInvestmentClasses(classes:any[]){
    this._investmentClasses = classes;
    this.investmentClasses.next(this._investmentClasses)
  }

  private _setFund(fund:any){
    this._fund = fund;
    this.fund.next(this._fund)
  }

  private _setPermissions(permissions:any){
    this._permissions = permissions;
    this.permissions.next(this._permissions)
  }

  private _setCREARoles(participantRoles:any[]){
    this._CREAParticipantRoles = participantRoles;
    this.CREAParticipantRoles.next(this._CREAParticipantRoles)
  }

  private _setThirdPartyRoles(participantRoles:any[]){
    this._thirdParticipantRoles = participantRoles;
    this.thirdParticipantRoles.next(this._thirdParticipantRoles)
  }

  private _setStatusSequences(sequences:any[]){
    this._sequenceStatuses = sequences;
    this.sequenceStatuses.next(this._sequenceStatuses)
  }

  private _setSyndicationsSequences(sequences:any[]){
    this._syndicationsSequences = sequences;
    this.syndicationsSequences.next(this._syndicationsSequences)
  }

  private _setFundProjectStates(states:any[]){
    this._fundProjectStates = states;
    this.fundProjectStates.next(this._fundProjectStates)
  }

  private _setFundTypes(types:any[]){
    this._fundTypes = types;
    this.fundTypes.next(this._fundTypes)
  }

  public updateInvestors(investor:any, investment:any, mode:string){
    if(mode==='delete'){
      this._investors = this._investors.filter((item:any) => item.id !== investor.id)
      this.setInvestors(this._investors)
    }
    if(mode==='update'){
      let updatedInv = investor
      updatedInv.ownershipInterest.ownershipInterestDetail = updatedInv.ownershipInterest.ownershipInterestDetail.map((detail:any) => detail.id === investment.id ? {...investment} : detail)
      let formattedInv = this._formatInvestor(updatedInv)
      let updatedList = this._investors.map((investor:any) => investor.id === formattedInv.id ? formattedInv : investor)
      this._investors = updatedList
      this.investors.next(updatedList)

    }
    if(mode=='add'){
      investor.ownershipInterest.ownershipInterestDetail.push(investment)
      let formattedInv = this._formatInvestor(investor)
      this._investors.push(formattedInv)
      this.investors.next(this._investors)
    }
  }

  private _formatParticipant(participant:any){
    let obj:any = {}
    if(participant.person!==null){
      obj = {
        id: participant.id,
        role: participant.role.name,
        role_id: participant.role.id,
        participant: participant.person.name,
        url: this.dataFormat.formatLink(participant.person),
        originatorRelationship: null,
      }
    } else {
      obj = {
        id: participant.id,
        role: participant.role.name,
        role_id: participant.role.id,
        participant: participant.organization===null ? participant.sponsor?.name : participant.organization?.name,
        url: participant.organization === null ? this.dataFormat.formatLink(participant.sponsor) : this.dataFormat.formatLink(participant.organization),
        sponsor: participant.organization!==null ? participant.organization.sponsor?.name : null,
        sponsorurl: participant.organization !== null && participant.organization.sponsor !== null ? this.dataFormat.formatLink(participant.organization.sponsor) : null,
        tags: null,
      }
    }
    return obj
  }

  private _setParticipants(participants:any[]){

    let creaParticipants = <any>[]
    let thirdPartyParticipants = <any>[]
    participants.forEach((participant:any) => {
      if(participant.person!==null){
        let formattedPart = this._formatParticipant(participant)
        creaParticipants.push(formattedPart)
      } else {
        let formattedPart = this._formatParticipant(participant)
        thirdPartyParticipants.push(formattedPart)
      }
    })
    creaParticipants.sort((a:any, b:any) => a.role.localeCompare(b.role))
    thirdPartyParticipants.sort((a:any, b:any) => a.role.localeCompare(b.role))
    this._participants['CREAParticipants']['dataSource'] = new MatTableDataSource<CREATable>(creaParticipants)
    this._participants['ThirdPartyParticipants']['dataSource'] = new MatTableDataSource<ThirdPartyTable>(thirdPartyParticipants)
    this.participants.next(this._participants)

  }

  public setGrossEquity = (outlet:any, projectCredit:any, isCertificated:boolean, projectStatus: string) => {
    let gross_equity = null;
    let isActualGrossEquity = false;

    if(projectCredit.project.fundProject!==null && projectCredit.project.fundProject.gross_equity!==null && projectCredit.project.fundProject.gross_equity!==0){
      gross_equity = projectCredit.project.fundProject.gross_equity;
      isActualGrossEquity = true;
      //actual gross equity
    }
    if(projectCredit.project.ltmodels.length>0){
      let index = null;
        for(let lt = 0; lt < projectCredit.project.ltmodels.length; lt++){
          let model = projectCredit.project.ltmodels[lt]
          if(model.kpiset!==null && model.kpiset.kpiset_detail!==null){
            if(model.active_model==true){
              index = lt
            } else if (model.milestone!==null && model.milestone.name=='Closed' && projectStatus=='Closed' && index==null){
              index = lt
              break;
            }
          }
        }

        if(index!==null){

          if(projectCredit.project.ltmodels[index].kpiset.pricePerCredits!==null && projectCredit.project.ltmodels[index].kpiset.pricePerCredits.length>0){
            for(let m = 0; m < projectCredit.project.ltmodels[index].kpiset.pricePerCredits.length; m++){

              if(projectCredit.project.ltmodels[index].kpiset.pricePerCredits[m].benefitType.name == (projectCredit.taxCreditType.level + " " + projectCredit.taxCreditType.name)){
                if(outlet.ownership!==null && projectCredit.project.ltmodels[index].kpiset.pricePerCredits[m].equity_amount!==null && !isActualGrossEquity){
                  gross_equity = outlet.ownership * .01 * projectCredit.project.ltmodels[index].kpiset.pricePerCredits[m].equity_amount * (isCertificated ? 1.04: 1.08)
                }
                break;
              }
            }
          }
        }

        // if(index!==null){
        //   let activeModel = projectCredit.project.ltmodels[index]
        //   if(activeModel.kpiset!==null && activeModel.kpiset.kpiset_detail!==null){
        //     gross_equity = outlet.ownership * .01 * activeModel.kpiset.kpiset_detail.total_equity * (isCertificated ? 1.04 : 1.08)
        //   }
        // }

    }

    return gross_equity;
  }

  public setEquity(project:any,credit:any, outlet:any, projectStatus:string){
    let equity = null
    if(project.ltmodels.length>0){
      let index = null;
      for(let lt = 0; lt < project.ltmodels.length; lt++){
        let model = project.ltmodels[lt]
        if(model.kpiset!==null && model.kpiset.kpiset_detail!==null){
          if(model.active_model==true){
            index = lt
          } else if (model.milestone!==null && model.milestone.name=='Closed' && projectStatus=='Closed' && index==null){
            index = lt
          }
        }
      }

      if(index!==null){
        let activeModel = project.ltmodels[index]
        if(activeModel.kpiset!==null && activeModel.kpiset.pricePerCredits!==null && activeModel.kpiset.pricePerCredits.length>0){
          for(let p = 0; p < activeModel.kpiset.pricePerCredits.length; p++){
            if(activeModel.kpiset.pricePerCredits[p].benefitType.name == (credit.taxCreditType.level + " " + credit.taxCreditType.name)){
              equity = parseFloat(activeModel.kpiset.pricePerCredits[p].equity_amount) * outlet.ownership * .01;
            }
          }
        }
      }
    }
    return equity;
  }

  public setPpc(project:any,credit:any, outlet:any, projectStatus:string){
    let ppc = null
    if(project.ltmodels.length>0){
      let index = null;
      for(let lt = 0; lt < project.ltmodels.length; lt++){
        let model = project.ltmodels[lt]
        if(model.kpiset!==null && model.kpiset.kpiset_detail!==null){
          if(model.active_model==true){
            index = lt
          } else if (model.milestone!==null && model.milestone.name=='Closed' && projectStatus=='Closed' && index==null){
            index = lt
          }
        }
      }

      if(index!==null){
        let activeModel = project.ltmodels[index]
        if(activeModel.kpiset!==null && activeModel.kpiset.pricePerCredits!==null && activeModel.kpiset.pricePerCredits.length>0){
          for(let p = 0; p < activeModel.kpiset.pricePerCredits.length; p++){
            if(activeModel.kpiset.pricePerCredits[p].benefitType.name == (credit.taxCreditType.level + " " + credit.taxCreditType.name)){
              ppc = activeModel.kpiset.pricePerCredits[p].price
              //equity = parseFloat(activeModel.kpiset.pricePerCredits[p].equity_amount) * outlet.ownership * .01;
            }
          }
        }
      }
    }
    return ppc;
  }

  public setHardDebt(project:any, projectStatus:any){
    let hard_debt = null
    if(project.ltmodels.length>0){
      let index = null;
      for(let lt = 0; lt < project.ltmodels.length; lt++){
        let model = project.ltmodels[lt]
        if(model.kpiset!==null && model.kpiset.kpiset_detail!==null){
          if(model.active_model==true){
            index = lt
          } else if (model.milestone!==null && model.milestone.name=='Closed' && projectStatus=='Closed' && index==null){
            index = lt
          }
        }
        if(index!==null){
          let activeModel = project.ltmodels[index]
          if(activeModel.kpiset!==null && activeModel.kpiset.kpiset_detail!==null){
            hard_debt = activeModel.kpiset.kpiset_detail.hard_debt;
          }
        }
      }

    }
    return hard_debt
  }

  private _setCredits(fund:any, isCertificated: boolean){
    let creditOutlets:any = {}
    let outlets = fund.projectCreditOutlets;

    let customOrder = ['Federal LIHTC', 'Federal Energy', 'Federal Historic', 'State LIHTC', 'State Energy', 'State Historic']


    for(let i = 0; i < outlets.length; i++){
      let state = outlets[i].fundProjectState
      let projObj = this._formatCredit(outlets[i], isCertificated, fund)
      if(creditOutlets.hasOwnProperty(state.name)){
        if(creditOutlets[state.name].hasOwnProperty(projObj.credit_type)){
          creditOutlets[state.name][projObj.credit_type].push(projObj)
          creditOutlets[state.name][projObj.credit_type].sort((a:any, b:any) => a.project < b.project ? -1 : 1)
        } else {
          creditOutlets[state.name][projObj.credit_type] = [projObj]
        }
      } else {
        creditOutlets[state.name] = {}
        creditOutlets[state.name][projObj.credit_type] = [projObj]
      }

    }
    let keySorted = {...this._sortCredits(creditOutlets)}
    let sorted = {...this._sortCreditKeys(keySorted)}
    this._outlets = sorted;
    this.outlets.next(this._outlets)
  }

  private _formatCredit(outlet:any, isCertificated:boolean, fund:any){
    let projectCredit = outlet.projectCredit;
    let project = outlet.projectCredit.project;
    let state = outlet.fundProjectState
    return {
      project: project.name,
      projecturl: this.dataFormat.formatLink(project),
      projectid: project.id,
      owned: outlet.ownership * .01,
      entity: project.ownedEntity.name,
      fundProjectState: state,
      credit_id: projectCredit.id,
      outlet_id: outlet.id,
      admitted_date: outlet.admitted_date ?? null,
      fundLoad: isCertificated ? fund.certificated_load : fund.non_certificated_load,
      is_target: outlet.is_target,
      notes: project.projectDetail.syndications_notes,
      exit_date: outlet.exit_date ?? null,
      is_target_exit: outlet.is_target_exit,
      credit_type: projectCredit.taxCreditType.level + " " + projectCredit.taxCreditType.name,
      credit_designation: projectCredit.taxCreditDesignation?.name,
      msa: this._setLocationString(project.properties[0].entity.entityAddresses[0].address, 'msa'),
      sponsor: project.sponsor[0]?.sponsor.nickname,
      sponsorurl: project.sponsor.length> 0 ? this.dataFormat.formatLink(project.sponsor[0].sponsor) : null,
      location: this._setLocationString(project.properties[0].entity.entityAddresses[0].address, 'location'),
      status: this._fetchProjectStatus(project.projectStatuses)?.name,
      syndicationsStatus: this._fetchProjectStatus(project.projectStatuses, true)?.name,
      project_status_id: this._fetchProjectStatus(project.projectStatuses)?.id,
      ppc: this.setPpc(project, projectCredit,outlet, this._fetchProjectStatus(project.projectStatuses).name),
      load: outlet.load_percentage ?? null,
      gross_equity: this.setGrossEquity(outlet, projectCredit, isCertificated, this._fetchProjectStatus(project.projectStatuses).name),///project.ltmodels.length > 0 ? setGrossEquity(outlets[i], projectCredit) : null,
      is_actual_gross_equity: projectCredit.project.fundProject!==null && projectCredit.project.fundProject.gross_equity!==null,
      actual_gross_equity: projectCredit.project.fundProject!== null ? projectCredit.project.fundProject.gross_equity : null,
      equity:this.setEquity(project, projectCredit,outlet, this._fetchProjectStatus(project.projectStatuses).name),
      estimated_gross_equity: outlet.estimated_gross_equity,
      equity_amount: projectCredit.equity_amount,
      yield_percent:project.ltmodels[0]?.kpiset.kpiset_detail?.lower_tier_yield ?? null,
      hard_debt: this.setHardDebt(project, this._fetchProjectStatus(project.projectStatuses).name),
      closing_date: project.pipelineProjects[0]?.closingDate[0]?.date ?? null,
      revenue_year: outlet.revenue_year ?? null,
      ut_irr: outlet.ut_irr ?? null,
      warehoused: outlet.warehoused,
      is_target_investor_approval: outlet.is_target_investor_approval,
      investor_approval_date: outlet.investor_approval_date,
      projected_warehouse_days: outlet.projected_warehouse_days,
      total_lihtc_units: project.properties[0].total_lihtc_units,
      total_units: project.properties[0].total_units,
      isExpanded: false
    }
  }

  private _sortCreditKeys(object:any) {
    let stateOrder = ['Admitted', 'Specified', 'Unspecified', 'Unlikely']
    let stateKeys = Object.keys(object)
    let sortedOrder:any = {}
    stateKeys.sort((a:string, b:string) => {
      const indexA = stateOrder.indexOf(a)
      const indexB = stateOrder.indexOf(b)
      if(indexA === -1 && indexB === -1){
        return a.localeCompare(b)
      } else if (indexA === -1){
        return 1
      } else if (indexB === -1){
        return -1;
      } else {
        return indexA - indexB
      }
    })
    for(const key of stateKeys){
      sortedOrder[key] = object[key];
    }
    return sortedOrder;
  }

  private _normalizeInvestorData(fund:any){
    let investorInterests:any = []
    for(let i = 0; i < fund.ownedEntities.length; i++){
      let entity = fund.ownedEntities[i]
      for(let u = 0; u < entity.ownershipInterests.length; u++){
        if(entity.ownershipInterests[u].fundInvestor!==null){
          let obj:any = {
            id: entity.ownershipInterests[u].organization?.id,
            fund_investor_id: entity.ownershipInterests[u].fundInvestor.id,
            investor: entity.ownershipInterests[u].organization?.name,
            comments: entity.ownershipInterests[u].fundInvestor.notes,
            is_repeat: entity.ownershipInterests[u].fundInvestor.is_repeat,
            ownership_id: entity.ownershipInterests[u].id,
            investments:[]
          }
          for(let o = 0; o < entity.ownershipInterests[u].ownershipInterestDetail.length; o++){
            let interestDetail = entity.ownershipInterests[u].ownershipInterestDetail[o];
            obj.investments.push({
              id: interestDetail.id,
              investor_class: interestDetail.investmentClass,
              amount: interestDetail.amount,
              admission_date: interestDetail.admission_date,
              exit_date: interestDetail.exit_date
            })
          }
          investorInterests.push(obj)
        }

      }
    }
    investorInterests.sort((a:any,b:any) => a.investor.localeCompare(b.investor))
    return investorInterests
  }

  private _formatInvestor(investor:any){
    let obj:any = {
      id: investor.ownershipInterest.organization?.id,
      fund_investor_id: investor.id,
      investor: investor.ownershipInterest.organization?.name,
      comments: investor.notes,
      is_repeat: investor.is_repeat,
      ownership_id: investor.ownershipInterest.id,
      investments:[]
    }
    investor.ownershipInterest.ownershipInterestDetail.forEach((d:any) => {
      obj.investments.push({
        id: d.id,
        investor_class: d.investmentClass,
        amount: d.amount,
        admission_date: d.admission_date,
        exit_date: d.exit_date
      })
    })
    return obj
  }

  private _sortCredits(credits:any){
    let customOrder = ['Federal LIHTC', 'Federal Energy', 'Federal Historic', 'State LIHTC', 'State Energy', 'State Historic']
    let sortedOrder:any = {}
    let statusKeys = Object.keys(credits)
    for(let s = 0; s < statusKeys.length; s++){
      let keys = Object.keys(credits[statusKeys[s]])
      keys.sort((k1:string,k2:string) => {
        const indexA = customOrder.indexOf(k1)
        const indexB = customOrder.indexOf(k2)
        if(indexA === -1 && indexB === -1){
          return k1.localeCompare(k2)
        } else if (indexA === -1){
          return 1
        } else if (indexB === -1){
          return -1;
        } else {
          return indexA - indexB
        }
      })
      const sortedObj:any = {}
      for(const key of keys){
        sortedObj[key] = credits[statusKeys[s]][key]
      }
      sortedOrder[statusKeys[s]] = sortedObj
    }
    return sortedOrder
  }

  public normalizeFormStrings(str: any) : string {
    if(!str) return "";
    if(typeof str === 'string') return str.trim();
    if(typeof str === 'object') return str.name.trim();
    return ""
  }

  private _setLocationString(address:any, type:string){
    let string = null
    if(type==='location'){
      let city = address.zipCode.city;
      let stateAbbr = address.zipCode.state.abbreviation;
      string = city + ", " + stateAbbr;
    } else {
      string = address.zipCode.county.metroStatAreaCounties[0]?.metroStatArea.name
    }
    return string;
  }

  private _fetchProjectStatus(statuses:any[], syndications?: boolean){
    let foundFundStatus = statuses.find((status:any) => {
      return status.active && syndications ? status.statusSequence.name == 'Syndications' : status.statusSequence.name == 'Funds'
    })

    if(foundFundStatus){
      return {name: foundFundStatus.status.name, id: foundFundStatus.id}
    } else {
      let activeStatus = statuses.filter((status:any) => status.active==true);
      return {name: activeStatus[0].status.name, id: activeStatus[0].id}
    }
  }

  public findDuplicateNames(groups: Array<{ value: any }>): { hasDuplicates: boolean; duplicates: string[] } {
    const counts = new Map<string, number>();
    for (const g of groups) {
      const n = this.normalizeFormStrings(g?.value?.name);
      if (!n) continue; counts.set(n, (counts.get(n) ?? 0) + 1);
    }
    const duplicates = Array.from(counts.entries()) .filter(([, c]) => c > 1) .map(([n]) => n);
    return { hasDuplicates: duplicates.length > 0, duplicates };
  }

  public setLocationString = (address:any, type:string) => {
    let string = null
    if(address==null){
      return string;
    }
    if(type==='location'){
      let city = address.zipCode.city;
      let stateAbbr = address.zipCode.state.abbreviation;
      string = city + ", " + stateAbbr;
    } else {
      string = address.zipCode.county.metroStatAreaCounties[0]?.metroStatArea.name
    }
    return string;
  }


  public updateFundDetails(info: any, callback:Function){
    this.apollo.mutate({
      mutation: update_fund_details,
      variables: {
        id: info.fund_id,
        input: {
          name: info.name,
          fund_type_id: info.type,
          commitments_required: info.commitments_required,
          invested_equity: info.totalCommittedEquity,
          close_date: info.closingDate == null ? null : this.datePipe.transform(info.closingDate, 'yyyy-MM-dd'),
          ein: info.ein,
          ic_code: info.ic_code
        },
        fund_status_id: info.fund_status_id,
        status_id: info.status,
        legal_entity_id: info.legal_entity_id,
        legal_name: info.legal_name,
      }
    }).subscribe(({data}:any) => {
      callback(data)
    })
  }

  public createManagingMemberEntity(ownedEntityTypeId:string | null, name: string, fundId: string, callback: Function, onError: Function){
    if(!ownedEntityTypeId){
      onError()
      return
    }
    this.apollo.mutate({
      mutation: create_fund_owned_entity,
      variables: {
        name: name,
        fund_id: fundId
      }
    }).subscribe({
      next: ({data}:any) => {
        this.addManagingMemberAttribute(ownedEntityTypeId, data.createFundOwnedEntity.id, callback, onError)
      },
      error: () => onError()
    })
  }

  public addManagingMemberAttribute(ownedEntityTypeId:string | null, entityId: string, callback: Function, onError: Function){
    if(!ownedEntityTypeId){
      onError()
      return
    }
    this.apollo.mutate({
      mutation: create_owned_entity_attribute,
      variables: {
        owned_entity_type_id: ownedEntityTypeId,
        owned_entity_id: entityId
      }
    }).subscribe({
      next: ({data}: any) => callback(data),
      error: () => onError()
    })
  }

  public updateManagingMemberEntity(details:any, callback:Function){
    this.apollo.mutate({
      mutation: update_fund_managing_member,
      variables: {
        id: details.managing_member_id,
        name: details.managing_member_name
      }
    }).subscribe((data:any) => {
      callback(data)
    })
  }

  public addBudgetRevenueYear(details:any, fundId: string, callback:Function){
    this.apollo.mutate({
      mutation: create_new_budget_revenue_detail,
      variables: {
        budget_amt: details.budget_amt,
        syndications_budget_year: details.syndications_budget_year,
        fund_id: fundId
      }
    }).subscribe((data:any) => callback(data))
  }

  public updateBudgetRevenueYear(details:any, callback: Function){
    this.apollo.mutate({
      mutation: update_budget_revenue_year,
      variables: {
        id: details.id,
        budget_amt: details.budget_amt,
        syndications_budget_year: details.syndications_budget_year
      }
    }).subscribe((data:any) => callback(data))
  }

  public removeRevenueYearRow(id:string, callback: Function){
    this.apollo.mutate({
      mutation: delete_budget_revenue_detail,
      variables: {id: id}
    }).subscribe((data:any) => callback(data));
  }

  public getCertificatedOutlets(id:string){
    // return this.apollo.query({
    //   query: get_certificated_outlet,
    //   variables: {id},
    //   fetchPolicy: 'network-only'
    // })
    let subscription = this.apollo.query({
      query: get_certificated_outlet,
      variables: {id},
      fetchPolicy: 'network-only'
    }).subscribe(({data}:any) => {
      let result:any = data;
      this._setFund(result.certificatedOutlet)
      this._setParticipants(result.certificatedOutlet.participants)
      this._setFundProjectStates(result.fund_project_states)
      let CREAParticipantRoles = result.CREARoles.roles.filter((role:any) => role.active)
      let thirdParticipantRoles = result.thirdPartyRoles.roles.filter((role:any) => role.active)
      this._setCREARoles(CREAParticipantRoles)
      this._setThirdPartyRoles(thirdParticipantRoles)
      this._setStatusSequences(result.statusesBySequence)
      this._setSyndicationsSequences(result.syndicationsSequence)
      this._setPermissions(result.me.fundPermissions)
      this._setCredits(result.certificatedOutlet, true)
      this._loaded = true;
      this.loaded.next(this._loaded)
      subscription.unsubscribe();
    })
  }

  public getFunds(id:string){
    // return this.apollo.query({
    //   query: get_fund,
    //   variables: {id},
    //   fetchPolicy: 'network-only'
    // })
    let subscription = this.apollo.query({
      query: get_fund,
      variables: {id},
      fetchPolicy: 'network-only'
    }).subscribe(({data}:any) => {
      let result:any = data;
      this._setFund(result.fund)
      this._setParticipants(result.fund.participants)
      let investments = this._normalizeInvestorData(result.fund)
      this.setInvestors(investments)
      this._setInvestmentClasses(result.investment_classes)
      this._investorTypeId = result.investor_type.id
      this.investorTypeId.next(this._investorTypeId)
      let CREAParticipantRoles = result.CREARoles.roles.filter((role:any) => role.active)
      let thirdParticipantRoles = result.thirdPartyRoles.roles.filter((role:any) => role.active)
      this._setCREARoles(CREAParticipantRoles)
      this._setFundTypes(result.fundTypes)
      this._setThirdPartyRoles(thirdParticipantRoles)
      this._setFundProjectStates(result.fund_project_states)
      this._setStatusSequences(result.statusesBySequence)
      this._setSyndicationsSequences(result.syndicationsSequence)
      this._setPermissions(result.me.fundPermissions)
      this._setCredits(result.fund, false)
      this._specialPurposeEntityId = result.owned_entity_type_by_name.id
      this.specialPurposeEntityId.next(this._specialPurposeEntityId)
      this._loaded = true;
      this.loaded.next(this._loaded)
      subscription.unsubscribe()
    })
  }

  public getParticipantSearch(searchText:any, role:string, callback: Function){
    this.apollo.watchQuery({
      query: search_active_employees,
      variables: {
        searchLike: '%' + searchText.trim() + '%',
        limit: 10,
        search: searchText.trim(),
        role:role
      }
    }).valueChanges.subscribe((data:any) => {
      callback(data)
    })
  }

  public getOrganizationSearch(searchText:any, role:string, callback: Function){
    this.apollo.watchQuery({
      query: search_organizations,
      variables: {
        name: '%' + searchText.trim() + '%',
        search: searchText.trim(),
      }
    }).valueChanges.subscribe((data:any) => {
      callback(data)
    })
  }

  public updateParticipants(participant:any, callback: Function){
    if(participant.person_id==null && participant.organization_id==null){
      return;
    }
    this.apollo.mutate({
      mutation: update_participants,
      variables: {
        id: participant.id,
        role_id: participant.role_id,
        person_id: participant.person_id,
        organization_id: participant.organization_id
      }
    }).subscribe((data) => {
      callback(data)
    })
  }

  public addParticipant(participant:any, isCertificated:boolean, callback:Function){
    if(participant.person_id==null && participant.organization_id==null){
      return;
    }
    let fundVar = {
      fund_id: participant.fund_id,
      role_id: participant.role_id,
      person_id: participant.person_id,
      organization_id: participant.organization_id
    }
    let certVar = {
      certificated_outlet_id: participant.fund_id,
      role_id: participant.role_id,
      person_id: participant.person_id,
      organization_id: participant.organization_id
    }
    this.apollo.mutate({
      mutation: isCertificated ? add_certificated_participant : add_fund_participant,
      variables: isCertificated ? certVar : fundVar
    }).subscribe((response:any) => callback(response))
  }

  public deleteParticipant(participantId: string, callback:Function) {
    this.apollo.mutate({
      mutation: remove_participant,
      variables: {
        id: participantId
      }
    }).subscribe((response:any) => callback(participantId))
  }

  public updateInterestDetail(detail:any, callback:Function){
    this.apollo.mutate({
      mutation: update_investment_detail,
      variables: {
        id: detail.id,
        investment_class_id: detail.investor_class,
        amount: detail.amount,
        admission_date: detail.admission_date,
        exit_date: detail.exit_date
      },
    }).subscribe((data:any) => callback(data))
  }

  public addInterestDetail(detail:any, callback:Function){
    this.apollo.mutate({
      mutation: create_investment_detail,
      variables: {
        ownership_interest_id: detail.ownership_id,
        investment_class_id: detail.investor_class,
        amount: detail.amount,
        admission_date: detail.admission_date,
        exit_date: detail.exit_date

      }
    }).subscribe((data:any) => callback(data));
  }

  public updateInvestor(detail:any, callback:Function){
    this.apollo.mutate({
      mutation: update_fund_investor,
      variables: {
        id: detail.id,
        is_repeat: detail.is_repeat,
        is_new: !detail.is_repeat,
        notes: detail.notes
      }
    }).subscribe((data:any) => callback(data))
  }

  public createFundInvestor(detail:any, callback:Function){
    this.apollo.mutate({
      mutation: create_fund_ownership_interest,
      variables: {
        owner_type_id: detail.owner_type_id,
        owned_entity_id: detail.owned_entity_id,
        organization_id: detail.organization_id
      }
    }).subscribe((response:any) => {
        this.apollo.mutate({
          mutation: create_fund_investor,
          variables: {
            ownership_interest_id: response.data.createInvestorOwnershipInterest.id,
            is_repeat: detail.is_repeat,
            is_pending: false,
            is_new: !detail.is_repeat,
            is_confirmed: true,
            notes: detail.notes
          }
        }).subscribe((response) => callback(response))
    })
  }

  public deleteInterestDetail(id: string, callback:Function){
    this.apollo.mutate({
      mutation: delete_fund_investment_detail,
      variables: {
        id: id
      }
    }).subscribe((data:any) => callback(data))
  }

  public deleteOwnershipInterest(fund_investor_id: string, ownership_interest_id: string, callback:Function){
    this.apollo.mutate({
      mutation: delete_fund_investor,
      variables: {
        fund_investor_id: fund_investor_id,
        ownership_interest_id: ownership_interest_id
      }
    }).subscribe((data:any) => callback(data))
  }

  public updateProjectCredit(detail:any, callback:Function){

    let outletVar = {
      id: detail.outlet_id,
      revenue_year: detail.revenue_year,
      fund_project_state_id: detail.fund_project_state_id,
      load_percentage: detail.load_percentage !== null ? detail.load_percentage * .01 : null,
      estimated_gross_equity: detail.estimated_gross_equity,
      ut_irr: detail.ut_irr !== null ? detail.ut_irr * .01 : null,
      ownership: detail.ownership,
      admitted_date: detail.admitted_date !== null ? this.datePipe.transform(detail.admitted_date, 'yyyy-MM-dd') : null,
      exit_date: detail.exit_date !== null ? this.datePipe.transform(detail.exit_date, 'yyyy-MM-dd') : null,
      is_target: detail.is_target,
      is_target_exit: detail.is_target_exit,
      warehoused: detail.warehoused,
      investor_approval_date: detail.investor_approval_date !== null ? this.datePipe.transform(detail.investor_approval_date, 'yyyy-MM-dd') : null,
      projected_warehouse_days: detail.projected_warehouse_days,
      is_target_investor_approval: detail.is_target_investor_approval,
      warehouse_funding_deadline_time: null,
      warehouse_funding_deadline_date: null,
      warehouse_funding_deadline_timezone: null,
    }

    this.apollo.mutate({
      mutation: update_project_credit,
      variables: {
        outlet_input: outletVar
      }
    }).subscribe((data:any) => callback(data))
  }

  public updateCreditsTable(mode: UpdateFundOutletCreditMode, credit:any, isCertified: boolean = false){
    if(mode===UpdateFundOutletCreditMode.ADD){

    }
    if(mode===UpdateFundOutletCreditMode.UPDATE){
      let taxType = credit.projectCredit.taxCreditType.level + " " + credit.projectCredit.taxCreditType.name;
      let formattedCredit = this._formatCredit(credit, isCertified, this._fund)
      this._outlets[credit.fundProjectState.name][taxType] = this._outlets[credit.fundProjectState.name][taxType].map((outlet:any) => outlet.outlet_id === credit.id ? {...formattedCredit} : outlet)
      this.outlets.next(this._outlets);
    }
    if(mode===UpdateFundOutletCreditMode.REMOVE){
      let taxType = credit.projectCredit.taxCreditType.level + " " + credit.projectCredit.taxCreditType.name;
      this._outlets[credit.fundProjectState.name][taxType] = this._outlets[credit.fundProjectState.name][taxType].filter((outlet:any) => outlet.outlet_id !== credit.id);
      if(this._outlets[credit.fundProjectState.name][taxType].length == 0){
        delete this._outlets[credit.fundProjectState.name][taxType]
        if(Object.keys(this._outlets[credit.fundProjectState.name]).length == 0){
          delete this._outlets[credit.fundProjectState.name]
        }
      }
      this.outlets.next(this._outlets)
    }
  }

  public updateBudgetYearTable(mode: UpdateFundBudgetYearMode, budgetline:any){
    if(mode===UpdateFundBudgetYearMode.ADD){
      let currentList = [...this._fund.syndicationsDetails];
      let currentFund = {...this._fund};
      currentList.push(budgetline)
      currentFund.syndicationsDetails = [...currentList]
      this._fund = currentFund
      this.fund.next(currentFund)
    }
    if(mode===UpdateFundBudgetYearMode.UPDATE){
      let updatedList = this._fund.syndicationsDetails.map((detail:any) => detail.id === budgetline.id ? {...budgetline} : detail);
      let updatedFund = {...this._fund}
      updatedFund.syndicationsDetails = [...updatedList]
      this._fund = updatedFund
      this.fund.next(updatedFund)
    }
    if(mode===UpdateFundBudgetYearMode.REMOVE){
      let updatedList = this._fund.syndicationsDetails.filter((detail:any) => detail.id !== budgetline.id)
      let updatedFund = {...this._fund}
      updatedFund.syndicationsDetails = [...updatedList]
      this._fund = updatedFund
      this.fund.next(updatedFund)
    }
  }

  public updateFundParticipantsTable(mode: UpdateFundOutletCreditMode, participant:any, keytype:string){
    if(mode===UpdateFundOutletCreditMode.ADD){
      let formattedParticipant = this._formatParticipant(participant)
      this._participants[keytype].dataSource.data.push(formattedParticipant)
      this.participants.next(this._participants)
    }
    if(mode===UpdateFundOutletCreditMode.UPDATE){
      let formattedParticipant = this._formatParticipant(participant)
      let updatedParticipants = this._participants[keytype].dataSource.data.map((part:any) => part.id === formattedParticipant.id ? {...formattedParticipant} : part)
      this._participants[keytype].dataSource.data = [...updatedParticipants]
      this.participants.next(this._participants)

    }
    if(mode===UpdateFundOutletCreditMode.REMOVE){
      let updatedList = this._participants[keytype].dataSource.data.filter((part:any)=> part.id !== participant)
      this._participants[keytype].dataSource.data = [...updatedList]
      this.participants.next(this._participants)
    }
  }


  public updateFundInformationData(response:any){
    let updatedFund = {...this._fund};
    updatedFund.name = response.updateFundDetails.name;
    updatedFund.legal_name = response.updateFundDetails.legal_name;
    updatedFund.ic_code = response.updateFundDetails.ic_code;
    updatedFund.ein = response.updateFundDetails.ein;
    updatedFund.close_date = response.updateFundDetails.close_date
    updatedFund.fundStatuses.map((status:any) => status.id === response.updateFundStatuses.id ? {...response.updateFundStatuses} : status)
    updatedFund.ownedEntities.map((entity:any) => entity.id === response.updateLegalName.id ? {...response.updateLegalName} : entity)
    this._fund = updatedFund;
    this.fund.next(updatedFund)
  }

  public createProjectCreditOutlet(details:any, callback:Function){
    let outletVar = {
      project_credit_id: details.project_credit_id,
      fund_id: details.fund_id,
      certificated_outlet_id: details.certificated_outlet_id,
      fund_project_state_id: details.fund_project_state_id,
      ownership: details.ownership,
      estimated_gross_equity: details.estimated_gross_equity,
      gross_equity: details.gross_equity,
      purchase_amount: details.purchase_amount,
      admitted_date: details.admitted_date,
      exit_date: details.exit_date,
      is_target: details.is_target,
      is_target_exit: details.is_target_exit,
      revenue_year: details.revenue_year,
      is_revenue_flagged: details.is_revenue_flagged,
      load_percentage: details.load_percentage !== null ? details.load_percentage * .01 : null,
      ut_irr: details.ut_irr !== null ? details.ut_irr * .01 : null,
      warehoused: details.warehoused,
      investor_approval_date: details.investor_approval_date !== null ? this.datePipe.transform(details.investor_approval_date, 'yyyy-MM-dd') : null,
      projected_warehouse_days: details.projected_warehouse_days,
      is_target_investor_approval: details.is_target_investor_approval,
      warehouse_funding_deadline_time: null,
      warehouse_funding_deadline_date: null,
      warehouse_funding_deadline_timezone: null,
    }
    this.apollo.mutate({
      mutation: create_project_credit_outlet,
      variables: {
        input: outletVar
      }
    }).subscribe((data) => callback(data))
  }

  public deleteProjectCredit(id:string, callback: Function){
    this.apollo.mutate({
      mutation: delete_project_credit_outlet,
      variables: {
        id: id
      }
    }).subscribe((data:any) => callback(data))
  }

  public getProjectSearch(searchText:string, callback:Function){
    this.apollo.watchQuery({
      query: credit_outlets_search_projects,
      variables: {
        searchLike: '%' + searchText.trim() + '%',
        search: searchText.trim()
      }
    }).valueChanges.subscribe((data:any) => callback(data))
  }
  public getOrgSearch(searchText:string, callback:Function){
    this.apollo.watchQuery({
      query: nav_search_organizations,
      variables: {
        searchLike: '%' + searchText.trim() + '%',
        search: searchText.trim(),
        limit: 10,
        page: 1
      }
    }).valueChanges.subscribe((data) => callback(data))
  }


}
